import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Package, TrendingUp, Shield, Download, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

// Product Showcase Slider Component
const ProductShowcaseSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      image: "https://customer-assets.emergentagent.com/job_paper-cups-showcase/artifacts/0r9pq6q2_IMG_1635.JPG",
      alt: "POS Cups Collection"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_paper-cups-showcase/artifacts/1np1oaa1_IMG_1470.JPG",
      alt: "POS Cups Display"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_paper-cups-showcase/artifacts/06b0j0xs_10%20Oz%20Cup.jpg",
      alt: "POS Cups 10oz"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Normal speed - every 4 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <>
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeInLeft {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out forwards;
        }
        .animate-fadeInLeft {
          animation: fadeInLeft 0.6s ease-out forwards;
        }
        .animate-pulse-slow {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
      <div className="mt-16 relative overflow-hidden rounded-2xl shadow-2xl" style={{ minHeight: '500px' }}>
      {/* Background Slideshow with Zoom Animation */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-700 ${
              currentSlide === index 
                ? 'opacity-100 scale-105' 
                : 'opacity-0 scale-100'
            }`}
            style={{
              backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.7) 35%, rgba(0, 0, 0, 0.3) 65%, rgba(0, 0, 0, 0.1) 100%), url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'right center',
              backgroundRepeat: 'no-repeat',
              transform: currentSlide === index ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 3s ease-in-out'
            }}
          />
        ))}
      </div>

      {/* Content Overlay with Slide Animation */}
      <div className="relative z-10 p-8 sm:p-12">
        <div className="max-w-2xl animate-fadeInLeft">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 drop-shadow-2xl animate-slideInLeft">
            Premium Hot Cups Collection
          </h2>
          <p className="text-white text-lg mb-6 drop-shadow-lg animate-slideInLeft" style={{animationDelay: '0.2s'}}>
            Choose from our signature POS Cups™ with color-coded bottoms for easy identification. Available in sizes from 8 oz to 24 oz.
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start">
              <span className="text-blue-300 mr-2 text-xl">✓</span>
              <span className="text-white drop-shadow-md">Retail Ready Packaging with GS-1 UPC barcodes</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-300 mr-2 text-xl">✓</span>
              <span className="text-white drop-shadow-md">Color-coded bottoms for quick size recognition</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-300 mr-2 text-xl">✓</span>
              <span className="text-white drop-shadow-md">1000 or 500 cups per case</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-300 mr-2 text-xl">✓</span>
              <span className="text-white drop-shadow-md">Bulk ordering available</span>
            </li>
          </ul>
          <a 
            href="https://customer-assets.emergentagent.com/job_b2b-packaging-1/artifacts/8kw17atq_POS%20Cups%20-%20Spec%20Sheet%20%281%29.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-600 hover:bg-blue-50 hover:scale-105 font-semibold px-6 py-3 rounded-lg shadow-2xl transition-all duration-300 animate-slideInLeft"
            style={{animationDelay: '0.6s'}}
            data-testid="download-spec-btn"
          >
            <Download className="mr-2 h-5 w-5" />
            Download Spec Sheet (PDF)
          </a>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 right-8 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                currentSlide === index 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

const Home = () => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success('Quote request submitted successfully! We\'ll get back to you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                Professional B2B Supplier Since 2020
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              POS Cups&trade; — Paper Hot Cups<br />
              <span className="text-blue-600">with UPC Barcode & Color-Coded Bottoms</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Premium paper hot cups and disposable packaging for restaurants, coffee shops, and food service companies. Value. Built In.
            </p>
          </div>

          {/* Product Showcase Slideshow */}
          <ProductShowcaseSlider />
        </div>
      </section>

      {/* Products Section */}
      <section id="products-section" className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Products</h2>
            <p className="text-lg text-gray-600">Professional paper hot cups for your business</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map(product => (
              <div key={product.id} className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all">
                <div className="bg-white rounded-lg mb-4 flex items-center justify-center overflow-hidden border-2 border-gray-200" style={{ height: '250px' }}>
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-contain p-4"
                    />
                  ) : (
                    <Package className="h-24 w-24 text-blue-600" />
                  )}
                </div>

                <div className="mb-3">
                  <span className="badge badge-blue">
                    {product.design === 'POS Cups™' ? 'POS Cups\u2122' : product.design}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>Case Quantity:</strong> {product.case_quantity} cups</p>
                  <p><strong>Packaging:</strong> {product.quantity_detail}</p>
                  <p><strong>Box Dimensions:</strong> {product.box_dimensions}</p>
                  <p><strong>Case Weight:</strong> {product.case_weight}</p>
                  <p><strong>Cases per Pallet:</strong> {product.cases_per_pallet}</p>
                  <p><strong>UPC:</strong> {product.upc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose DC PAC LLC?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 rounded-lg w-14 h-14 flex items-center justify-center mb-4">
                <TrendingUp className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Pricing</h3>
              <p className="text-gray-600">
                Best value for high-volume orders. Contact us for volume pricing and special rates for distributors.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 rounded-lg w-14 h-14 flex items-center justify-center mb-4">
                <Package className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Fulfillment</h3>
              <p className="text-gray-600">
                Quick order processing with pallet ordering options. Ideal for distributors and large food service operations.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 rounded-lg w-14 h-14 flex items-center justify-center mb-4">
                <Shield className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Guaranteed</h3>
              <p className="text-gray-600">
                Premium paper hot cups with retail-ready packaging and GS-1 UPC codes on every cup, sleeve, and case.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request a Quote Section */}
      <section id="quote-section" className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="quote-section">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Request a Quote</h2>
            <p className="text-lg text-gray-600">Tell us about your needs and we'll provide a custom quote</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="John Doe"
                    data-testid="quote-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="john@company.com"
                    data-testid="quote-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="ABC Restaurant"
                    data-testid="quote-company-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="(555) 123-4567"
                    data-testid="quote-phone-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message / Requirements *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none resize-none"
                  placeholder="Please tell us about your requirements: product quantities, delivery timeline, special requests, etc."
                  data-testid="quote-message-input"
                />
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Need help choosing the right products?</strong><br />
                  Our team can recommend the best cup sizes and quantities for your business type.
                </p>
              </div>

              <Button 
                type="submit"
                disabled={submitting}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                data-testid="submit-quote-btn"
              >
                <Send className="mr-2 h-5 w-5" />
                {submitting ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-8 bg-gray-50 rounded-xl shadow-md p-6 text-center border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Prefer to Talk Directly?</h3>
            <p className="text-gray-600 mb-4">Our sales team is ready to help you</p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Phone:</strong> <a href="tel:732-997-0792" className="text-blue-600 hover:underline">732-997-0792</a></p>
              <p><strong>Email:</strong> <a href="mailto:info@dcpac.co" className="text-blue-600 hover:underline">info@dcpac.co</a></p>
              <p><strong>Address:</strong> 6 Kilmer Rd #1118, Edison, NJ 08817</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;